import { type Accessor, For, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import MaskInput from '~/components/MaskInput'
import SwipeAction from '~/components/SwipeAction'
import { toNumber } from '~/services/product.service'
import type { DraftTariffType } from '~/types/ProductForm'
import type { FormControllerType, FormValuesType } from '~/types/useForm'

export interface PreciosTabPropsType<T extends FormValuesType = FormValuesType> {
  tariffs: DraftTariffType[]
  form: FormControllerType<T>
  basePrice: () => number
  money: (value: unknown) => string
  isService: Accessor<boolean>
  openCreateTariff: () => void
  openEditTariff: (index: number) => void
  removeTariff: (index: number) => void
}

/** Pestaña Precios: tarifas por tipo de cliente, precio anterior, coste e impuesto. */
function PreciosTab<T extends FormValuesType>(props: PreciosTabPropsType<T>) {
  return (
    <div class="space-y-5">
      <div>
        <div class="flex items-center justify-between mb-2">
          <div>
            <h3 class="font-semibold text-foreground">Tarifas</h3>
            <p class="text-xs text-foreground-subtle">Un precio por tipo de cliente. La marcada es la que se propone al cotizar.</p>
          </div>
          <Button size="sm" variant="soft" color="default" onClick={props.openCreateTariff} prependIcon="material-symbols-light:add">
            Añadir tarifa
          </Button>
        </div>

        <div class="space-y-2">
          <For each={props.tariffs}>
            {(tariff, tariffIndex) => (
              <SwipeAction
                rightOptions={[
                  { icon: 'material-symbols-light:edit-outline', action: () => props.openEditTariff(tariffIndex()), color: 'warning', label: 'Editar' },
                  { icon: 'material-symbols-light:delete-outline', action: () => props.removeTariff(tariffIndex()), color: 'danger', label: 'Eliminar' }
                ]}
                fullSwipe={false}
                onClick={() => props.openEditTariff(tariffIndex())}
              >
                <div class={`flex items-center gap-3 rounded-lg border px-4 py-3 cursor-pointer ${tariff.isDefault ? 'border-primary bg-primary-soft/20' : 'border-border'}`}>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-foreground truncate">{tariff.name}</span>
                      <Show when={tariff.isDefault}>
                        <Badge color="primary" variant="soft" size="sm">
                          Por defecto
                        </Badge>
                      </Show>
                    </div>
                    <p class="text-xs text-foreground-subtle mt-0.5">
                      {tariff.tiers
                        .slice()
                        .sort((a, b) => a.minQuantity - b.minQuantity)
                        .map(t => `${t.minQuantity.toLocaleString('es-CO')}+ → $${t.amount.toLocaleString('es-CO')}`)
                        .join(' · ')}
                    </p>
                  </div>
                  <Icon icon="material-symbols-light:chevron-right" size={20} class="text-foreground-subtle shrink-0" />
                </div>
              </SwipeAction>
            )}
          </For>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MaskInput
          name="compareAtPrice"
          label="Precio anterior"
          maskType="currency"
          currency="COP"
          locale="es-CO"
          fractionDigits={0}
          min={0}
          value={String(props.form.values().compareAtPrice ?? '')}
          onValueChange={v => props.form.setValue('compareAtPrice', v === '' ? null : Number(v))}
          helperText="Para mostrar el descuento"
        />
        <MaskInput
          name="cost"
          label="Coste"
          maskType="currency"
          currency="COP"
          locale="es-CO"
          fractionDigits={0}
          min={0}
          value={String(props.form.values().cost ?? '')}
          onValueChange={v => props.form.setValue('cost', v === '' ? null : Number(v))}
          helperText="No se muestra al cliente"
        />
        <MaskInput
          name="taxRate"
          label="Impuesto (%)"
          maskType="percentage"
          min={0}
          max={100}
          value={String(props.form.values().taxRate ?? '')}
          onValueChange={v => props.form.setValue('taxRate', v === '' ? null : Number(v))}
        />
        <Input name="unit" label="Unidad" placeholder={props.isService() ? 'servicio, hora, mes' : 'unidad, caja, kg'} />
      </div>

      <Show when={toNumber(props.form.values().cost) > 0 && props.basePrice() > 0}>
        <div class="rounded-lg bg-surface-muted px-4 py-3 text-sm">
          <span class="text-foreground-subtle">Margen sobre la tarifa por defecto: </span>
          <span class="font-medium text-foreground">
            {props.money(props.basePrice() - toNumber(props.form.values().cost))} ({Math.round(((props.basePrice() - toNumber(props.form.values().cost)) / props.basePrice()) * 100)}%)
          </span>
        </div>
      </Show>
    </div>
  )
}

export default PreciosTab
